import { parseImageRef, isAcrHost } from "./preconditions.js";
import { deriveAcrName, ensureAcr, ensureImageInAcr } from "./acr.js";
import type { FoundryProject } from "./foundry-project.js";

/** Resource-group segment from an ARM account scope id. */
function resourceGroupFromScope(scope: string): string {
  return /\/resourceGroups\/([^/]+)\//.exec(scope)?.[1] ?? "";
}

export interface StageResult {
  image: string; // the ref to deploy (ACR ref when staged; unchanged otherwise)
  staged: boolean;
  notes: string[];
}

/**
 * Post-2026-06-25 Foundry projects reject anonymous image pulls — the platform
 * authenticates to the registry with the project managed identity. So a public
 * image (e.g. ghcr.io) is staged into the per-customer ACR (create-if-missing +
 * server-side import) and the ACR ref is returned. An ACR ref is passed through
 * unchanged (the bring-your-own-registry path).
 */
export async function stagePublicImageIfNeeded(args: {
  image: string;
  project: FoundryProject;
  /**
   * OPTIONAL `sha256:<hex>` to import FROM, instead of the mutable tag in `image`.
   *
   * `image` stays `<ref>:<tag>` even when this is set, and deliberately: it is
   * what `parseImageRef` derives the destination repo and tag from, and a digest
   * ref cannot supply those (parseImageRef splits on ':', so
   * `ghcr.io/x/y@sha256:abc` parses as repo `…y@sha256` with tag `abc`). So the
   * DESTINATION stays tagged, for whoever reads the ACR later, while the SOURCE
   * is pinned to exact bytes — which is the half that matters.
   *
   * Absent ⇒ import from the tag, unchanged. The converge rail passes nothing
   * and behaves exactly as before.
   *
   * KNOWN, PRE-EXISTING: `ensureImageInAcr` short-circuits when the destination
   * tag already exists, so this does not re-import over a tag already in the ACR
   * with different bytes. That is fine for the from-zero install this was added
   * for (the ACR is created moments earlier) and is not made worse here.
   */
  digest?: string;
  onProgress?: (m: string) => void;
}): Promise<StageResult> {
  const notes: string[] = [];
  const { host, repo, tag } = parseImageRef(args.image);
  if (isAcrHost(host)) return { image: args.image, staged: false, notes };

  const sourceRef = args.digest ? `${host}/${repo}@${args.digest}` : args.image;
  const acrName = deriveAcrName(args.project.accountName);
  const resourceGroup = resourceGroupFromScope(args.project.accountScope);
  args.onProgress?.(`staging ${sourceRef} into ${acrName} (server-side import)…`);
  const acr = await ensureAcr({ acrName, resourceGroup, location: args.project.region });
  if (acr.created) notes.push(`created ACR '${acrName}' in ${resourceGroup} for hosted-agent images.`);
  const { imported } = await ensureImageInAcr({ acrName, sourceRef, repo, tag });
  const acrRef = `${acr.loginServer}/${repo}:${tag}`;
  notes.push(
    imported
      ? `imported ${sourceRef} → ${acrRef} (server-side, no local build).`
      : `image already present in ${acrName}; using ${acrRef}.`,
  );
  return { image: acrRef, staged: true, notes };
}
